/**
 * Fetch Affine SSO credentials from Control Plane
 *
 * Uses allow_guest + access_token body-param pattern (same as fetchChatwootSso,
 * domain.py, auth.py). Frappe's validate_auth rejects unknown Bearer headers
 * on non-allow_guest endpoints, so we pass the JWT as a body parameter.
 *
 * The CP method auto-provisions if workspace doesn't exist yet,
 * returning { provisioned_now: true } on first access.
 */

import { getCPToken } from "@/lib/api/controlPlaneClient";
import type { AffineSsoResponse } from "./types";

const CONTROL_PLANE_URL =
  import.meta.env.VITE_CONTROL_PLANE_URL || "https://control.zaviago.com";

export async function fetchAffineSso(
  tenantName: string
): Promise<AffineSsoResponse> {
  const token = getCPToken();
  if (!token) {
    throw new Error("Not authenticated to Control Plane");
  }

  const response = await fetch(
    `${CONTROL_PLANE_URL}/api/method/control_plane.services.affine_api.get_affine_sso_url`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify({
        access_token: token,
        tenant_name: tenantName,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    let message = "Failed to get Affine SSO URL";
    if (error._server_messages) {
      try {
        const msgs = JSON.parse(error._server_messages);
        let parsed = msgs[0];
        if (typeof parsed === "string") {
          try {
            parsed = JSON.parse(parsed);
          } catch {
            /* use as-is */
          }
        }
        message =
          typeof parsed === "object" && parsed?.message
            ? String(parsed.message)
            : String(parsed);
      } catch {
        message = String(error._server_messages);
      }
    } else if (error.message) {
      message = error.message;
    }
    throw new Error(message);
  }

  const data = await response.json();
  return data.message as AffineSsoResponse;
}
